import { Button, HStack, Skeleton, Stack, Text } from '@chakra-ui/react';
import { useCallback } from 'react';
import { useTranslations } from 'use-intl';

import { Exceptions } from '@blockpulse3/data/shared';
import {
  CompanyCapitalType,
  FundraisingInvestStatus,
  FundraisingStatus,
  SubscriptionInfosFragment,
  SubscriptionStepStatus,
  SubscriptionStepType,
  SubscriptionWorkflowType,
  UserRole,
  useGetSubscriptionQuery,
  useValidateCompletedSubscriptionMutation,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import { Progress, ProgressStep, useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';
import { useStepStatusMap } from '@blockpulse3/ui/ui-hooks';
import { useUser } from '@blockpulse3/web-client/auth';

import { ResetDocumentSignature } from './DocumentSignatureStep/ResetDocumentSignature';
import { ConsultIdentityVerification } from './IdentityVerificationStep/ConsultIdentityVerification';
import { ConsultMifid, ValidateMifid } from './MifidStep';
import { ValidatePayment } from './PaymentStep/ValidatePayment';
import { ValidateFundsSourceCertificate } from './SubscriptionDetailsStep/ValidateFundsSourceCertificate';

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
};

export function SubscriptionProgressSteps({ subscriptionId }: Props): JSX.Element {
  const i18nSubscriberDetailsSidePanel = useTranslations('SubscriberDetailsSidePanel');

  /* ** Toasts ** */
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { user } = useUser();
  const { data, loading, refetch } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });
  const subscription = data?.subscription;

  const [validateCompletedSubscription, { loading: validateLoading }] =
    useValidateCompletedSubscriptionMutation();

  const [stepStatusMap, steps] = useStepStatusMap(subscription?.data?.mergedSteps);

  const IdentityVerificationStep = useCallback(
    (): JSX.Element | null => <ConsultIdentityVerification subscriptionId={subscriptionId} />,
    [subscriptionId],
  );

  const balance = subscription?.bankVirtualIban?.balance || 0;

  const subscriptionStepTypes =
    subscription?.data.mergedSteps?.map((step) => {
      return step.type;
    }) || [];

  const isDetailsStepValidated =
    stepStatusMap[SubscriptionStepType.DETAILS] === SubscriptionStepStatus.VALID;
  const isPaymentStepValidated =
    stepStatusMap[SubscriptionStepType.PAYMENT] === SubscriptionStepStatus.VALID;
  const isDocumentStepValidated =
    stepStatusMap[SubscriptionStepType.DOCUMENT_SIGNATURE] === SubscriptionStepStatus.VALID;
  const isDocumentStepStarted =
    stepStatusMap[SubscriptionStepType.DOCUMENT_SIGNATURE] !== SubscriptionStepStatus.NONE;
  const investAmount = subscription?.investAmount;
  const isAllInSubscriptionWorkflow =
    subscription?.fundraising.subscriptionWorkflowType === SubscriptionWorkflowType.ALL_IN;

  const MifidStep = (): JSX.Element | null => {
    const mifidStepStatus = stepStatusMap[SubscriptionStepType.MIFID];
    if (mifidStepStatus === SubscriptionStepStatus.VALID) {
      return <ConsultMifid subscriptionId={subscriptionId} />;
    }
    if (mifidStepStatus === SubscriptionStepStatus.PENDING) {
      return (
        <HStack>
          <ConsultMifid subscriptionId={subscriptionId} />;
          <ValidateMifid subscriptionId={subscriptionId} />
        </HStack>
      );
    }
    return null;
  };

  const PaymentStep = (): JSX.Element | null => {
    if (!investAmount) return null;

    const isFixedCapital =
      subscription.fundraising.company.capitalType === CompanyCapitalType.FIXED;
    const isExternalIBAN = subscription.fundraising.isExternalIBAN;

    if (isPaymentStepValidated) {
      return (
        <Text color="gray.800" fontSize="xs" fontWeight="600">
          {i18nSubscriberDetailsSidePanel('ValidationSteps.Payment.Subtitle', {
            amount: formatNumberCurrency(investAmount),
          })}
        </Text>
      );
    } else if (
      isFixedCapital &&
      isExternalIBAN &&
      isDocumentStepValidated &&
      (!isAllInSubscriptionWorkflow ||
        subscription.fundraising.status === FundraisingStatus.REVIEWED)
    ) {
      return <ValidatePayment subscriptionId={subscriptionId} />;
    } else if (balance) {
      return (
        <Text color="gray.800" fontSize="xs" fontWeight="600">
          {i18nSubscriberDetailsSidePanel('ValidationSteps.Payment.Subtitle', {
            amount: formatNumberCurrency(balance),
          })}
        </Text>
      );
    }
    return null;
  };

  const SubscriptionDetailsStep = (): JSX.Element | null => {
    if (
      isDetailsStepValidated &&
      subscription &&
      ![FundraisingInvestStatus.APPROVED, FundraisingInvestStatus.FINALIZED].includes(
        subscription.investStatus,
      )
    ) {
      return <ValidateFundsSourceCertificate subscriptionId={subscriptionId} />;
    }
    return null;
  };

  const DocumentSignatureStep = (): JSX.Element | null => {
    if (
      isDocumentStepStarted &&
      subscription &&
      ![FundraisingStatus.CLOSED, FundraisingStatus.REVIEWED, FundraisingStatus.FINALIZED].includes(
        subscription.fundraising.status,
      )
    ) {
      return <ResetDocumentSignature subscriptionId={subscriptionId} />;
    }
    return null;
  };

  const shouldDisplayValidateSubscription =
    subscription?.investStatus === FundraisingInvestStatus.COMPLETED &&
    (user?.roles.includes(UserRole.SUPERADMIN) ||
      user?.roles.includes(UserRole.COMPLIANCE_OFFICER));

  /* ** On validate subscription button click */
  const handleValidateCompletedSubscription = (): void => {
    validateCompletedSubscription({
      variables: {
        subscriptionId,
      },
      onCompleted: () => {
        successToast({
          title: i18nSubscriberDetailsSidePanel('Toast.ValidateSubscriptionSuccess'),
        });
        refetch();
      },
      onError: (err: unknown) => {
        const error = err as Error;
        if (error.message === Exceptions.ExceedsHardCap) {
          errorToast({
            title: i18nSubscriberDetailsSidePanel('Toast.ValidateSubscriptionExceedsHardCap'),
          });
        } else {
          errorToast({ title: i18nSubscriberDetailsSidePanel('Toast.ValidateSubscriptionError') });
        }
      },
    });
  };

  return (
    <Stack px="4" spacing="4">
      <Text color="gray.600" fontWeight="600">
        {i18nSubscriberDetailsSidePanel('ValidationSteps.Title')}
      </Text>
      {loading ? (
        <Stack spacing="2">
          <Skeleton h="30px" w="60%" />
          <Skeleton h="30px" w="80%" />
          <Skeleton h="30px" w="65%" />
          <Skeleton h="30px" w="70%" />
        </Stack>
      ) : (
        <>
          <Progress direction="column">
            <ProgressStep
              key={0}
              index={0}
              textProps={{ fontSize: 'sm', fontWeight: '500', color: 'gray.500' }}
              title={i18nSubscriberDetailsSidePanel('ValidationSteps.CreateAccount')}
              status={
                subscription?.isSignedUp
                  ? SubscriptionStepStatus.VALID
                  : SubscriptionStepStatus.NONE
              }
            />
            <>
              {steps
                .filter((step) => subscriptionStepTypes.includes(step.type))
                .map((step) => (
                  <ProgressStep
                    key={step.index}
                    index={step.index}
                    status={step.status}
                    textProps={{ fontSize: 'sm', fontWeight: '500', color: 'gray.500' }}
                    title={step.title}
                  >
                    <>
                      {step.type === SubscriptionStepType.MIFID && <MifidStep />}
                      {step.type === SubscriptionStepType.IDENTITY_VERIFICATION && (
                        <IdentityVerificationStep />
                      )}
                      {step.type === SubscriptionStepType.DETAILS && <SubscriptionDetailsStep />}
                      {step.type === SubscriptionStepType.DOCUMENT_SIGNATURE && (
                        <DocumentSignatureStep />
                      )}
                      {step.type === SubscriptionStepType.PAYMENT && <PaymentStep />}
                    </>
                  </ProgressStep>
                ))}
            </>
          </Progress>
          {shouldDisplayValidateSubscription && (
            <Button
              isLoading={validateLoading}
              size="sm"
              variant="secondary"
              onClick={handleValidateCompletedSubscription}
            >
              {i18nSubscriberDetailsSidePanel('ValidationSteps.ValidateSubscription')}
            </Button>
          )}
        </>
      )}
    </Stack>
  );
}
